import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Send, Eye, X, AlertTriangle, CheckCircle2, Printer } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");
const STATUS = { sent: ["Awaiting signature", "bg-gold-light text-gold-dark"], signed: ["Signed", "bg-sage-light text-sage-dark"], superseded: ["Replaced", "bg-secondary text-ink-muted"] };
const TITLE = { freelance: "Freelance agreement", employment: "Contract of employment", zero_hours: "Zero-hours contract" };
const CHOICE = { freelance: ["Chose freelance", "bg-brand-light text-brand"], zero_hours: ["Chose zero-hours", "bg-sage-light text-sage-dark"] };
const titleOf = (a) => TITLE[a?.type || "freelance"];

export function AgreementText({ terms }) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-ink" data-testid="staff-agreement-terms">
      {terms.split("\n\n").map((p, i) => {
        const m = p.match(/^(\d+\.\s[^.]+\.)\s([\s\S]*)$/);
        return <p key={i}>{m ? <><span className="font-semibold">{m[1]}</span> {m[2]}</> : p}</p>;
      })}
    </div>
  );
}

function AgreementDoc({ a }) {
  return (
    <div id="staff-agreement-print" className="space-y-4">
      <div className="border-b border-border pb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand">Brightside Befriending Services Ltd</p>
        <h3 className="font-heading text-xl font-bold text-ink" data-testid="staff-agreement-title">{titleOf(a)} — {a.staff_name}</h3>
        <p className="text-xs text-ink-muted">Version {a.version} · issued {fmt(a.created_at)} by {a.issued_by}{a.weekly_hours ? ` · ${a.weekly_hours} hrs/week` : ""}</p>
      </div>
      <AgreementText terms={a.terms} />
      <div className="grid grid-cols-2 gap-6 border-t border-border pt-4 text-sm">
        <div><p className="text-xs text-ink-muted">For Brightside Befriending Services Ltd</p><p className="font-semibold">{a.issued_by}</p></div>
        <div><p className="text-xs text-ink-muted">Signed by {a.staff_name}</p><p className="font-semibold">{a.status === "signed" ? a.signed_name : "Not signed yet"}</p></div>
      </div>
      {a.status === "signed" && (
        <p className="rounded-lg bg-sage-light px-3 py-2 text-sm text-sage-dark" data-testid="staff-agreement-signature">Signed online by <span className="font-semibold">{a.signed_name}</span> on {fmt(a.signed_at)}</p>
      )}
    </div>
  );
}

const printDoc = () => {
  const html = document.getElementById("staff-agreement-print")?.innerHTML || "";
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>Brightside agreement</title><style>body{font-family:Arial,sans-serif;max-width:720px;margin:32px auto;color:#1f2937;font-size:13px;line-height:1.5}h3{margin:4px 0}</style></head><body>${html}</body></html>`);
  w.document.close(); w.print();
};

export function Modal({ a, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="staff-agreement-modal">
        <div className="mb-3 flex justify-end gap-2">
          <button onClick={printDoc} data-testid="staff-agreement-print-button" className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold"><Printer size={13} /> Print</button>
          <button onClick={onClose} data-testid="staff-agreement-close"><X size={20} className="text-ink-muted" /></button>
        </div>
        <AgreementDoc a={a} />
      </div>
    </div>
  );
}

function ChoicePicker({ r, onChoose }) {
  const c = r.contract_choice;
  const issued = r.agreement && r.agreement.type === c && r.agreement.status !== "superseded";
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs" data-testid={`choice-picker-${r.staff_id}`}>
      <span className="font-semibold text-ink">Contract choice:</span>
      <select value={c || "none"} onChange={(e) => onChoose(r, e.target.value, false)} data-testid={`choice-select-${r.staff_id}`}
        className="rounded-lg border border-border bg-white px-2 py-1 text-xs outline-none focus:border-brand">
        <option value="none">No reply yet</option>
        <option value="freelance">Freelance</option>
        <option value="zero_hours">Zero-hours contract</option>
      </select>
      {c && <span className={`rounded-full px-2 py-0.5 font-semibold ${CHOICE[c][1]}`} data-testid={`choice-badge-${r.staff_id}`}>{CHOICE[c][0]}{r.contract_choice_by === "self" ? " in the app" : ""}{r.contract_choice_at ? ` · ${fmt(r.contract_choice_at)}` : ""}</span>}
      {c && !issued && <button onClick={() => onChoose(r, c, true)} data-testid={`issue-choice-${r.staff_id}`} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1 font-semibold text-white hover:bg-brand-dark"><Send size={12} /> Issue {TITLE[c].toLowerCase()} to sign</button>}
      {c && issued && <span className="text-ink-muted" data-testid={`choice-issued-${r.staff_id}`}>{TITLE[c]} issued — {STATUS[r.agreement.status][0].toLowerCase()}</span>}
    </div>
  );
}

function Row({ r, onSend, onView, onChoose, canSendOffice }) {
  const a = r.agreement;
  const [label, tone] = a ? STATUS[a.status] : ["Not sent", "bg-slate-100 text-slate-500"];
  const recent = r.weeks.map((w) => w.hours);
  const allowed = r.role === "carer" || canSendOffice;
  const employed = r.employed;
  return (
    <div data-testid={`staff-agreement-row-${r.staff_id}`} className="space-y-3 rounded-xl border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink">{r.name} <span className="text-xs font-normal text-ink-muted">· {r.role === "carer" ? "Befriender" : "Office staff"}</span></p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
          <span className={`rounded-full px-2 py-0.5 font-semibold ${tone}`} data-testid={`staff-agreement-status-${r.staff_id}`}>{a ? `${titleOf(a)} · ` : ""}{label}{a ? ` · v${a.version}` : ""}</span>
          <span className="text-ink-muted">{r.role === "carer" ? `Last 8 weeks: ${recent.join(" · ")} hrs` : `${r.contracted_hours ?? 0} hrs / week`}</span>
          {r.needs_contract && !employed && !r.contract_choice && <span data-testid={`needs-contract-${r.staff_id}`} className="inline-flex items-center gap-1 rounded-full bg-coral-light px-2 py-0.5 font-semibold text-coral-dark"><AlertTriangle size={11} /> {r.role === "carer" ? "Over 15 hrs for 4 weeks" : "Set at over 15 hrs a week"} — offer a contract</span>}
        </div>
      </div>
      {a && <button onClick={() => onView(a)} data-testid={`view-staff-agreement-${r.staff_id}`} className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary"><Eye size={13} /> View</button>}
      {allowed && !employed && <button onClick={() => onSend(r, "freelance")} data-testid={`send-staff-agreement-${r.staff_id}`} className="inline-flex items-center gap-1 rounded-lg border border-brand px-3 py-1.5 text-xs font-semibold text-brand hover:bg-brand-light"><Send size={13} /> {a?.type === "freelance" ? "New freelance version" : "Send freelance"}</button>}
      {allowed && <button onClick={() => onSend(r, "employment")} data-testid={`offer-contract-${r.staff_id}`} className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-white ${r.needs_contract && !employed ? "bg-coral-dark" : "bg-brand"}`}><FileSignature size={13} /> {a?.type === "employment" ? "New contract version" : "Offer fixed-hours contract"}</button>}
      </div>
      {allowed && <ChoicePicker r={r} onChoose={onChoose} />}
    </div>
  );
}

const OPTIONS = [
  { key: "freelance", title: "Freelance (self-employed)", points: ["Paid every 2 weeks, no tax or NI taken off", "You do your own HMRC Self Assessment", "7 days' notice either side"] },
  { key: "zero_hours", title: "Zero-hours employment contract", points: ["No set hours, no limit, accept or turn down work", "Tax and NI through PAYE", "Paid holiday, sick pay and pension", "3 months' probation, then 4 weeks' notice"] },
];

export function MyContractChoice() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/me/contract-choice").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const pick = async (choice) => {
    if (!window.confirm(`Choose ${TITLE[choice].toLowerCase()}? The office will then issue your contract to sign.`)) return;
    setBusy(choice);
    try { await api.put("/me/contract-choice", { choice }); toast.success("Thank you — your choice has been sent to the office"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-brand/30 bg-brand-light/20 p-6 shadow-sm lg:col-span-2" data-testid="my-contract-choice">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><FileSignature size={18} className="text-brand" /> Choose your contract</h2>
      <p className="mb-4 text-sm text-ink-soft">{d.choice ? `You chose ${TITLE[d.choice].toLowerCase()} on ${fmt(d.chosen_at)}.` : "Please choose by Thursday 22nd October 2026."} Pay is the same on both: £14/hr companion visits, £16/hr respite, care home and hospital, £14 a week per telephone client, and 45p a mile between clients, paid separately.{d.locked ? " Your contract has been issued — contact the office to change it." : ""}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {OPTIONS.map((o) => {
          const sel = d.choice === o.key;
          return (
            <div key={o.key} data-testid={`choice-option-${o.key}`} className={`rounded-lg border bg-white p-4 ${sel ? "border-brand ring-2 ring-brand/30" : "border-border"}`}>
              <p className="font-semibold text-ink">{o.title}</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-ink-soft">{o.points.map((p) => <li key={p}>{p}</li>)}</ul>
              {sel ? <p className="mt-3 flex items-center gap-1 text-sm font-semibold text-sage-dark" data-testid={`chosen-${o.key}`}><CheckCircle2 size={15} /> Your choice</p>
                : !d.locked && <button onClick={() => pick(o.key)} disabled={!!busy} data-testid={`choose-${o.key}`} className="mt-3 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">{d.choice ? "Change to this" : "Choose this"}</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReminderBar() {
  const [st, setSt] = useState(null);
  const load = () => api.get("/staff-agreements/choice-reminder").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const sendNow = async () => {
    if (!window.confirm(`Email a reminder now to the ${st.undecided.length} staff who haven't chosen?`)) return;
    try { const r = await api.post("/staff-agreements/choice-reminder"); toast.success(`Reminder sent to ${r.data.sent.length}${r.data.failed.length ? `, ${r.data.failed.length} failed` : ""}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-ivory px-3 py-2 text-xs text-ink-soft" data-testid="choice-reminder-bar">
      <span data-testid="choice-reminder-status">{st.sent_at ? `Reminder emailed ${fmt(st.sent_at)}.` : "A friendly reminder emails automatically on Wednesday 21st October at 9am to anyone who hasn't chosen."} Not chosen yet: {st.undecided.length ? st.undecided.join(", ") : "nobody"}.</span>
      {st.undecided.length > 0 && <button onClick={sendNow} data-testid="send-choice-reminder-now" className="rounded-lg border border-brand px-2.5 py-1 font-semibold text-brand hover:bg-brand-light">Send reminder now</button>}
    </div>
  );
}

export function StaffAgreementsAdmin({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [view, setView] = useState(null);
  const [showTpl, setShowTpl] = useState("");
  const load = () => api.get("/staff-agreements").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/staff-agreements/template").then((r) => setTpl(r.data)).catch(() => {}); }, []);
  const send = async (r, type) => {
    let weekly_hours = null;
    if (type === "employment") {
      const h = window.prompt(`Contracted hours per week for ${r.name}:`, String(r.contracted_hours || 16));
      if (h === null) return;
      weekly_hours = parseFloat(h);
      if (!(weekly_hours > 0 && weekly_hours <= 48)) { toast.error("Enter weekly hours between 1 and 48"); return; }
    } else if (!window.confirm(`Send the freelance agreement to ${r.name} to sign?${r.agreement ? " This replaces anything awaiting signature." : ""}`)) return;
    try { await api.post("/staff-agreements", { staff_id: r.staff_id, type, weekly_hours }); toast.success(`${TITLE[type]} sent to ${r.name}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const choose = async (r, choice, issue) => {
    if (issue && !window.confirm(`Issue the ${TITLE[choice].toLowerCase()} to ${r.name} to sign in the app?${r.agreement?.status === "sent" ? " This replaces anything awaiting signature." : ""}`)) return;
    try {
      await api.put(`/staff/${r.staff_id}/contract-choice`, { choice, issue });
      toast.success(issue ? `${TITLE[choice]} sent to ${r.name}` : choice === "none" ? "Choice cleared" : `Recorded: ${r.name} chose ${TITLE[choice].toLowerCase()}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const counts = { freelance: 0, zero_hours: 0, none: 0 };
  rows.forEach((r) => { counts[r.contract_choice || "none"] += 1; });
  return (
    <div className="space-y-4" data-testid="staff-agreements-admin">
      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-ink"><FileSignature size={18} className="text-brand" /> Staff agreements</h2>
            <p className="text-sm text-ink-soft">Each person chooses freelance (self-employed) or a zero-hours employment contract (PAYE, no set hours). Record their reply below, then issue the matching contract to sign in the app. Each document is personalised with their name, role, pay rates and start date.</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold" data-testid="choice-counts">
              <span className="rounded-full bg-brand-light px-2 py-0.5 text-brand" data-testid="count-freelance">Freelance: {counts.freelance}</span>
              <span className="rounded-full bg-sage-light px-2 py-0.5 text-sage-dark" data-testid="count-zero-hours">Zero-hours: {counts.zero_hours}</span>
              <span className="rounded-full bg-gold-light px-2 py-0.5 text-gold-dark" data-testid="count-no-reply">No reply yet: {counts.none}</span>
            </div>
            <ReminderBar />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowTpl(showTpl === "terms" ? "" : "terms")} data-testid="toggle-staff-agreement-template" className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary">{showTpl === "terms" ? "Hide" : "Read"} freelance terms</button>
            <button onClick={() => setShowTpl(showTpl === "zero_hours_terms" ? "" : "zero_hours_terms")} data-testid="toggle-zero-hours-template" className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary">{showTpl === "zero_hours_terms" ? "Hide" : "Read"} zero-hours contract</button>
            <button onClick={() => setShowTpl(showTpl === "employment_terms" ? "" : "employment_terms")} data-testid="toggle-employment-template" className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink hover:bg-secondary">{showTpl === "employment_terms" ? "Hide" : "Read"} fixed-hours contract</button>
          </div>
        </div>
        {showTpl && tpl && <div className="mt-4 border-t border-border pt-4"><p className="mb-3 text-xs text-ink-muted">Words in [brackets] are filled in for each person when you send it. Please have the wording checked by an employment adviser before you rely on it.</p><AgreementText terms={tpl[showTpl]} /></div>}
      </div>
      {rows.map((r) => <Row key={r.staff_id} r={r} onSend={send} onView={setView} onChoose={choose} canSendOffice={isAdmin} />)}
      {view && <Modal a={view} onClose={() => setView(null)} />}
    </div>
  );
}

export function ContractsList({ url, empty = "No contracts or agreements yet." }) {
  const [docs, setDocs] = useState(null);
  const [view, setView] = useState(null);
  useEffect(() => { api.get(url).then((r) => setDocs(r.data)).catch(() => setDocs([])); }, [url]);
  if (docs === null) return <p className="text-sm text-ink-muted">Loading…</p>;
  return (
    <div className="space-y-2" data-testid="contracts-list">
      {docs.length === 0 && <p className="text-sm text-ink-muted" data-testid="contracts-empty">{empty}</p>}
      {docs.map((a) => {
        const [label, tone] = STATUS[a.status] || STATUS.sent;
        return (
          <div key={a.id} data-testid={`contract-${a.id}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3">
            <FileSignature size={16} className="shrink-0 text-brand" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink">{titleOf(a)} · v{a.version}</p>
              <p className="text-xs text-ink-muted">Issued {fmt(a.created_at)}{a.signed_at ? ` · signed ${fmt(a.signed_at)}` : ""}{a.weekly_hours ? ` · ${a.weekly_hours} hrs/week` : ""}</p>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>
            <button onClick={() => setView(a)} data-testid={`view-contract-${a.id}`} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-ink hover:bg-secondary"><Eye size={13} /> View</button>
          </div>
        );
      })}
      {view && <Modal a={view} onClose={() => setView(null)} />}
    </div>
  );
}

export function MyContracts() {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm lg:col-span-2" data-testid="my-contracts">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><FileSignature size={18} className="text-brand" /> My contracts</h2>
      <p className="mb-3 text-sm text-ink-soft">Every agreement and contract you've been sent, newest first. Open one to read or print it.</p>
      <ContractsList url="/me/staff-agreements" empty="You haven't been sent a contract yet." />
    </div>
  );
}

export function MyStaffAgreement() {
  const [a, setA] = useState(undefined);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/me/staff-agreement").then((r) => setA(r.data)).catch(() => setA(null));
  useEffect(() => { load(); }, []);
  if (!a) return null;
  const sign = async () => {
    setBusy(true);
    try { await api.post(`/me/staff-agreement/${a.id}/sign`, { signed_name: name, agree }); toast.success("Thank you — your agreement is signed"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-xl border p-6 shadow-sm lg:col-span-2 ${a.status === "sent" ? "border-gold/50 bg-gold-light/20" : "border-border bg-white"}`} data-testid="my-staff-agreement">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><FileSignature size={18} className="text-brand" /> My {titleOf(a).toLowerCase()}</h2>
      {a.status === "signed" ? (
        <p className="flex items-center gap-1.5 text-sm text-sage-dark" data-testid="my-staff-agreement-signed"><CheckCircle2 size={15} /> Version {a.version} signed on {fmt(a.signed_at)}. <button onClick={() => setOpen(true)} className="font-semibold text-brand underline" data-testid="my-staff-agreement-view">View</button></p>
      ) : (
        <>
          <p className="mb-3 text-sm text-ink-soft">Please read your agreement and sign it below.</p>
          <div className="max-h-96 overflow-y-auto rounded-lg border border-border bg-white p-4"><AgreementText terms={a.terms} /></div>
          <div className="mt-4 space-y-3">
            <label className="flex items-start gap-2 text-sm text-ink"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="my-staff-agreement-agree" className="mt-1" /> I have read and agree to this {titleOf(a).toLowerCase()}.</label>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-ink-muted">Type your full name to sign<input value={name} onChange={(e) => setName(e.target.value)} data-testid="my-staff-agreement-name" className="block w-64 rounded-lg border border-border px-3 py-2 text-sm text-ink outline-none focus:border-brand" /></label>
              <button disabled={busy || !agree || !name.trim()} onClick={sign} data-testid="my-staff-agreement-sign" className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Sign agreement</button>
            </div>
          </div>
        </>
      )}
      {open && <Modal a={a} onClose={() => setOpen(false)} />}
    </div>
  );
}
